import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { env } from 'src/config/env'
import AuthSubmitButton from 'src/features/auth/components/auth-submit-button'
import AuthSubtitle from 'src/features/auth/components/auth-subtitle'
import AuthTitle from 'src/features/auth/components/auth-title'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { CodeFormField } from 'src/shared/components/ui/forms/form-fields/code'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { useConsumerSignUp, useConsumerConfirmSignUp, useConsumerResendCode } from 'src/shared/features/auth/api'
import CustomCheckbox from 'src/shared/features/users/components/marketplace/auth-forms/custom-checkbox'
import { useToast } from 'src/shared/hooks/use-toast'
import {
  consumerSignupSchema,
  type ConsumerSignupFormData,
  confirmationCodeSchema,
  type ConfirmationCodeFormData,
} from 'src/shared/schemas/forms/consumer-auth'
import { useEventStore } from 'src/shared/store/event-store'

import AuthCodeInput from '../auth-code-input'
import AuthInputFormField from '../auth-input-form-field'

interface ConsumerSignupFormProps {
  onSuccess: () => void
  title?: string
  variant?: 'default' | 'version2'
  onStepChange?: (step: 'signup' | 'verify') => void
}

const ConsumerSignupForm = ({ onSuccess, title, variant = 'default', onStepChange }: ConsumerSignupFormProps) => {
  const [step, setStep] = useState<'signup' | 'verify'>('signup')
  const [email, setEmail] = useState('')
  const [termsAgreed, setTermsAgreed] = useState(false)
  const { toast } = useToast()
  // gclid is used indirectly in useConsumerSignUp and useConsumerConfirmSignUp hooks
  const { gclid, fbclid } = useEventStore() // Get gclid from event store

  // Signup form
  const signupForm = useForm<ConsumerSignupFormData>({
    resolver: zodResolver(consumerSignupSchema),
    mode: 'onSubmit',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
    },
  })

  // Verification code form
  const verificationForm = useForm<ConfirmationCodeFormData>({
    resolver: zodResolver(confirmationCodeSchema),
    mode: 'onSubmit',
    defaultValues: {
      code: '',
    },
  })

  // Hooks for signup process
  const signUp = useConsumerSignUp()
  const confirmSignUp = useConsumerConfirmSignUp()
  const resendCode = useConsumerResendCode()

  // Handle signup form submission
  const handleSignupSubmit = async (data: ConsumerSignupFormData) => {
    try {
      await signUp.mutateAsync({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phoneNumber,
        gclid,
        fbclid,
      })

      // Store email for verification and resending
      setEmail(data.email)

      // Move to verification step
      setStep('verify')
      onStepChange?.('verify')

      toast({
        title: 'Verification code sent',
        description: 'Please check your email for the verification code',
        duration: 5000,
      })
    } catch (error) {
      console.error('Signup error:', error)

      // Handle specific errors
      if (error instanceof Error) {
        if (error.name === 'UsernameExistsException') {
          toast({
            title: 'Account already exists',
            description: 'An account with this email already exists. Please log in instead.',
            variant: 'destructive',
          })
          return
        }

        // Show specific error message to help troubleshoot
        toast({
          title: `Error: ${error.name}`,
          description: error.message || 'Failed to create account',
          variant: 'destructive',
        })
      } else {
        // Generic error
        toast({
          title: 'Signup failed',
          description: 'An unexpected error occurred. Please try again.',
          variant: 'destructive',
        })
      }
    }
  }

  // Handle verification code submission
  const handleVerificationSubmit = async (data: ConfirmationCodeFormData) => {
    try {
      if (!data.code || data.code.length < 6) {
        console.error('Verification code is incomplete:', data.code)
        toast({
          title: 'Incomplete code',
          description: 'Please enter the complete 6-digit code',
          variant: 'destructive',
        })
        return
      }

      // Get form data from the signup form
      const formValues = signupForm.getValues()

      await confirmSignUp.mutateAsync({
        email,
        code: data.code,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        phoneNumber: formValues.phoneNumber,
      })

      toast({
        title: 'Account created successfully',
        description: 'Your account has been verified and created',
      })

      onSuccess()
    } catch (error) {
      console.error('Verification error:', error)

      if (error instanceof Error) {
        toast({
          title: `Verification failed: ${error.name}`,
          description: error.message || 'Failed to verify account',
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Verification failed',
          description: 'An unexpected error occurred. Please try again with a new code.',
          variant: 'destructive',
        })
      }
    }
  }

  // Handle resend code
  const handleResendCode = async () => {
    try {
      await resendCode.mutateAsync(email)

      toast({
        title: 'Code resent',
        description: 'A new verification code has been sent to your email',
      })
    } catch (error) {
      console.error('Resend code error:', error)

      if (error instanceof Error) {
        toast({
          title: `Failed to resend code: ${error.name}`,
          description: error.message || 'Unable to send a new code',
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Failed to resend code',
          description: 'An unexpected error occurred. Please try again.',
          variant: 'destructive',
        })
      }
    }
  }

  if (variant === 'version2') {
    return (
      <div className="mx-auto mt-4 max-w-[500px]">
        {step === 'signup' ? (
          <>
            {/* <div className="mb-6 flex flex-col gap-1 text-center">
              <h2 className="text-[26px] font-semibold tracking-[-0.26px]">Create Account</h2>
              <p className="text-[15px] font-medium text-muted">{'Where homeownership happens.'}</p>
            </div> */}
            <AuthTitle title="Create Account" />
            <AuthSubtitle description="Where homeownership happens." />
            <Form {...signupForm}>
              <form onSubmit={signupForm.handleSubmit(handleSignupSubmit)} className="flex flex-col gap-3">
                <div className="flex flex-row gap-[10px]">
                  <AuthInputFormField
                    control={signupForm.control}
                    name="firstName"
                    label="First name"
                    placeholder="First name"
                    type="text"
                  />

                  <AuthInputFormField
                    control={signupForm.control}
                    name="lastName"
                    label="Last name"
                    placeholder="Last name"
                    type="text"
                  />
                </div>
                <AuthInputFormField
                  control={signupForm.control}
                  name="phoneNumber"
                  label="Phone number"
                  placeholder="Phone number"
                  type="tel"
                />

                <AuthInputFormField
                  control={signupForm.control}
                  name="email"
                  label="Email address"
                  placeholder="Email address"
                  type="email"
                />
                {/* <div className="mt-0">
                  <p className="text-center text-xs text-muted">
                    We&apos;ll email you a code to confirm your email.{' '}
                    <a
                      href={env.LINK_TO_PRIVACY_POLICY}
                      target="_blank"
                      className="font-medium underline hover:text-primary"
                      rel="noreferrer"
                    >
                      Privacy Policy
                    </a>
                  </p>
                </div> */}
                <AuthSubmitButton type="submit" isLoading={signUp.isPending} className="mt-4">
                  {signUp.isPending ? 'Creating Account...' : 'Continue'}
                </AuthSubmitButton>
              </form>
            </Form>
          </>
        ) : (
          <Form {...verificationForm}>
            <form
              onSubmit={verificationForm.handleSubmit(handleVerificationSubmit)}
              className="mx-auto max-w-[500px] space-y-4"
            >
              <div>
                <AuthTitle title="Confirm your email" />
                <AuthSubtitle
                  description={`You've received a code at ${email}. Please check your inbox and spam folders to retrieve it.`}
                  className="mb-0"
                />

                <AuthCodeInput
                  isConsumerLogin={false}
                  control={verificationForm.control}
                  name="code"
                  className="py-2"
                  inputClassName="h-[55px] border-none bg-accent rounded-[12px]"
                />

                <div
                  className="flex items-start gap-2 pt-2 hover:cursor-pointer"
                  onClick={() => setTermsAgreed(!termsAgreed)}
                >
                  {/* <input
                  type="checkbox"
                  id="terms"
                  className="mt-1 size-4"
                  checked={termsAgreed}
                  onChange={(e) => setTermsAgreed(e.target.checked)}
                /> */}
                  <CustomCheckbox checked={termsAgreed} onChange={(e) => setTermsAgreed(e)} />
                  <label htmlFor="terms" className="text-xs text-muted">
                    I confirm I am 18+ and consent to: (1) sharing my information with real estate professionals; (2)
                    receiving communications from Conrad and these professionals via email, calls, and texts; and (3)
                    the collection of my data as per the{' '}
                    <a
                      href={env.LINK_TO_PRIVACY_POLICY}
                      target="_blank"
                      className="font-medium text-primary underline"
                      rel="noreferrer"
                    >
                      Privacy Policy
                    </a>
                    . I agree to the{' '}
                    <a
                      href={env.LINK_TO_TERMS_OF_USE}
                      target="_blank"
                      className="font-medium text-primary underline"
                      rel="noreferrer"
                    >
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a
                      href={env.LINK_TO_PRIVACY_POLICY}
                      target="_blank"
                      className="font-medium text-primary underline"
                      rel="noreferrer"
                    >
                      Privacy Policy
                    </a>
                    , which will be updated for consumer use. I can manage my preferences in settings.
                  </label>
                </div>
              </div>

              <div className="flex flex-col">
                <AuthSubmitButton
                  type="submit"
                  className="mt-4"
                  isLoading={confirmSignUp.isPending}
                  disabled={!verificationForm.watch('code') || !termsAgreed}
                >
                  {confirmSignUp.isPending ? 'Verifying...' : 'Verify & sign up'}
                </AuthSubmitButton>

                <Button
                  type="button"
                  variant="ghost"
                  className="text-sm text-muted hover:underline"
                  onClick={handleResendCode}
                  isLoading={resendCode.isPending}
                >
                  {resendCode.isPending ? 'Resending...' : 'Resend code'}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </div>
    )
  }

  return (
    <div className="mt-6 space-y-4">
      {step === 'signup' ? (
        <>
          <h2 className="text-center text-3xl font-bold text-primary">{title || 'Sign up to Conrad!'}</h2>
          <Form {...signupForm}>
            <form onSubmit={signupForm.handleSubmit(handleSignupSubmit)}>
              <InputFormField
                control={signupForm.control}
                name="firstName"
                label="First name"
                placeholder="First name"
                type="text"
                required
              />

              <InputFormField
                control={signupForm.control}
                name="lastName"
                label="Last name"
                placeholder="Last name"
                type="text"
                required
              />

              <InputFormField
                control={signupForm.control}
                name="phoneNumber"
                label="Phone number"
                placeholder="Phone number"
                type="tel"
                required
              />

              <InputFormField
                control={signupForm.control}
                name="email"
                label="Email address"
                placeholder="Email address"
                type="email"
                required
              />
              <div className="mt-4">
                <p className="text-xs text-muted">
                  We&apos;ll email you a code to confirm your email.{' '}
                  <a href={env.LINK_TO_PRIVACY_POLICY} className="font-medium text-primary underline">
                    Privacy Policy
                  </a>
                </p>
              </div>
              <Button type="submit" className="mt-6 w-full py-4" isLoading={signUp.isPending}>
                {signUp.isPending ? 'Creating Account...' : 'Continue'}
              </Button>
            </form>
          </Form>
        </>
      ) : (
        <Form {...verificationForm}>
          <form onSubmit={verificationForm.handleSubmit(handleVerificationSubmit)} className="space-y-4">
            <h3 className="text-center text-3xl font-bold text-primary">Confirm your email</h3>
            <p className="text-center text-sm text-muted">
              You&apos;ve received a code at {email}. Please check your inbox and spam folders to retrieve it.
            </p>

            <CodeFormField control={verificationForm.control} name="code" className="py-2" length={6} />

            <div className="flex items-start gap-2 pt-2">
              <input
                type="checkbox"
                id="terms"
                className="mt-1 size-4"
                checked={termsAgreed}
                onChange={(e) => setTermsAgreed(e.target.checked)}
              />
              <label htmlFor="terms" className="text-xs text-muted">
                I confirm I am 18+ and consent to: (1) sharing my information with real estate professionals; (2)
                receiving communications from Conrad and these professionals via email, calls, and texts; and (3) the
                collection of my data as per the{' '}
                <a href={env.LINK_TO_PRIVACY_POLICY} className="font-medium text-primary underline">
                  Privacy Policy
                </a>
                . I agree to the{' '}
                <a href={env.LINK_TO_TERMS_OF_USE} className="font-medium text-primary underline">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href={env.LINK_TO_PRIVACY_POLICY} className="font-medium text-primary underline">
                  Privacy Policy
                </a>
                , which will be updated for consumer use. I can manage my preferences in settings.
              </label>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                type="submit"
                className="w-full rounded-md bg-primary py-4 text-white hover:bg-primary/80"
                isLoading={confirmSignUp.isPending}
                disabled={!verificationForm.watch('code') || !termsAgreed}
              >
                {confirmSignUp.isPending ? 'Verifying...' : 'Verify & sign up'}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="text-sm text-muted hover:underline"
                onClick={handleResendCode}
                isLoading={resendCode.isPending}
              >
                {resendCode.isPending ? 'Resending...' : 'Resend code'}
              </Button>
            </div>
          </form>
        </Form>
      )}
    </div>
  )
}

export default ConsumerSignupForm
